import React from 'react';

import { Button, ButtonGroup, DatePicker, Flex, Heading, Loader, Modal, Table, Text } from '@sine/sine-ui-components';

import { useManualManageModal } from '../hooks/useManualManageModal.hook';
import { ManualManageModalRow } from './ManualManageModalRow';

interface ManageModalProps {
  onClose: (refresh?: boolean) => void;
  siteId?: string;
  /** Fallback site object for newly created sites not yet in billing data */
  site?: { id: string; name: string };
}

export default function ManualManageModal({ onClose, site, siteId }: ManageModalProps) {
  const { state, values, handlers } = useManualManageModal({
    onClose,
    siteId,
    site,
  });

  const isSingleSite = !!siteId;
  const siteName = isSingleSite ? values.lineItems[0]?.site?.name : undefined;

  return (
    <>
      <Modal
        show
        size='fullScreen'
        onClose={() => onClose()}
        header={
          <Flex direction='column'>
            <Heading data-st-ext='heading-manage-subscription' type='h2'>
              {isSingleSite ? 'Manage manual license' : 'Manually manage'}
            </Heading>
            <Text colorVariant='text68' variant='small'>
              {siteName ? `Manage license for ${siteName}` : 'Make adjustments to your plans.'}
            </Text>
          </Flex>
        }
        footer={
          <Flex justifyContent='space-between'>
            <Button
              variant='secondary'
              icon='arrow-open'
              onClick={() => window.open('https://www.sine.co/pricing/', '_blank')}
              data-st-ext='button-pricing-page'
            >
              View complete feature comparison
            </Button>
            <ButtonGroup alignment='right'>
              <Button
                variant={state.isLoading ? 'disabled' : 'tertiary'}
                onClick={() => onClose()}
                data-st-ext='button-cancel'
              >
                Cancel
              </Button>
              <Button
                variant={'primary'}
                onClick={handlers.createOrUpdateManualLicense}
                data-st-ext='button-add-licenses'
              >
                {isSingleSite ? 'Save' : 'Add licenses'}
              </Button>
            </ButtonGroup>
          </Flex>
        }
      >
        <Loader overlay show={state.isLoading} />

        <Flex direction='row' gap={3} fullWidth>
          <Flex direction='column' gap={3} fullWidth>
            <Flex direction='row' gap={2} fullWidth>
              <DatePicker
                noSpaceReservation
                label='From'
                value={state.startDate}
                onChange={(value) => handlers.setStartDate(value as Date)}
                data-st-ext='manual-subscription-start-date'
              />
              <DatePicker
                noSpaceReservation
                label='To'
                value={state.endDate}
                onChange={(value) => handlers.setEndDate(value as Date)}
                minDate={state.startDate}
                data-st-ext='manual-subscription-end-date'
              />
            </Flex>
            <Table variant='rounded'>
              <Table.Head>
                <Table.Row>
                  <Table.HeaderCell>Site</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Start Date</Table.HeaderCell>
                  <Table.HeaderCell>End Date</Table.HeaderCell>
                  <Table.HeaderCell>Visitors Plan</Table.HeaderCell>
                  <Table.HeaderCell>Contractors Plan</Table.HeaderCell>
                  <Table.HeaderCell>Notes</Table.HeaderCell>
                  <Table.HeaderCell>{/* Actions Cell Placeholder */}</Table.HeaderCell>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {values.lineItems.map((lineItem) => {
                  const key = lineItem.id ?? lineItem.site?.id ?? `${lineItem.site?.name}`;
                  return (
                    <ManualManageModalRow
                      key={key}
                      lineItem={lineItem}
                      onPlanChange={handlers.updateLineItem}
                      onRemove={handlers.removeLineItem}
                      onNoteChange={handlers.handleNoteChange}
                      planOptionsForRow={values.planOptions}
                    />
                  );
                })}
              </Table.Body>
            </Table>

            <Flex justifyContent='flex-end'>
              <Button
                onClick={handlers.addLineItem}
                variant='secondary'
                icon='add'
                size='sm'
                data-st-ext='button-add-license'
              >
                Add License
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Modal>
    </>
  );
}
